import React, { useEffect, useMemo, useRef, useState } from "react";
import { get810Reconciliation } from "../utils/ediApi";

const SUMMARY = [
  ["expected", "Expected"], ["accepted", "Accepted"], ["waiting", "Waiting"],
  ["blocked", "Blocked"], ["missing", "Missing"], ["rejectedOrAttention", "Rejected / Attention"],
];

const STATUS_LABELS = {
  MISSING_810: "Missing 810",
  READY_TO_GENERATE: "Ready to Generate",
  READY_TO_REVIEW: "Ready to Review",
  READY_TO_SEND: "Ready to Send",
  GENERATED_UNSENT: "Generated, Unsent",
  SUBMITTING: "Submitting",
  AWAITING_MDN: "Waiting for AS2 Confirmation",
  AWAITING_997: "Waiting for Walmart 997",
  ACCEPTED: "Accepted",
  COMPLETED: "Accepted",
  BLOCKED: "Blocked",
  PREVIEW_BLOCKED: "Blocked",
  WALMART_REJECTED: "Walmart Rejected",
  IT_ATTENTION_REQUIRED: "IT Attention Required",
};

export function localBusinessDate() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

const shown = (value) => value === null || value === undefined || value === "" ? "—" : String(value);

export default function Walmart810Reconciliation({ headers, onOpenBatch }) {
  const [shippedDate, setShippedDate] = useState(localBusinessDate());
  const [value, setValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const mounted = useRef(true);
  const requestId = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; requestId.current += 1; };
  }, []);

  const rows = useMemo(() => Array.isArray(value?.rows) ? value.rows : [], [value]);

  const reconcile = async (event) => {
    event.preventDefault();
    if (!shippedDate) return;
    const id = ++requestId.current;
    const requestedDate = shippedDate;
    setLoading(true); setError("");
    try {
      const next = await get810Reconciliation(requestedDate, { headers });
      if (mounted.current && id === requestId.current) setValue(next);
    } catch (exception) {
      if (mounted.current && id === requestId.current) {
        setValue(null);
        setError(exception?.message || "Unable to load Walmart 810 reconciliation.");
      }
    } finally {
      if (mounted.current && id === requestId.current) setLoading(false);
    }
  };

  const changeDate = (nextDate) => {
    requestId.current += 1;
    setShippedDate(nextDate); setValue(null); setError(""); setLoading(false);
  };

  return <section className="space-y-5" aria-labelledby="reconciliation-810-title">
    <h2 id="reconciliation-810-title" className="sr-only">Walmart 810 Reconciliation</h2>
    <form onSubmit={reconcile} className="flex flex-wrap items-end gap-3 rounded-lg border bg-white p-4">
      <label className="text-sm font-medium">Shipped Date
        <input aria-label="810 Reconciliation Shipped Date" type="date" required value={shippedDate} onChange={(event) => changeDate(event.target.value)} className="mt-1 block rounded border px-3 py-2"/>
      </label>
      <button type="submit" disabled={loading || !shippedDate} className="rounded bg-blue-700 px-4 py-2 text-white disabled:opacity-50">{loading ? "Reconciling..." : "Reconcile Date"}</button>
    </form>
    {error && <div role="alert" className="rounded bg-red-50 p-3 text-sm text-red-800">{error}</div>}
    {value && <>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {SUMMARY.map(([key, label]) => <div key={key} className="rounded-lg border bg-white p-3"><div className="text-xs font-semibold uppercase text-slate-500">{label}</div><div className="mt-1 text-2xl font-bold text-slate-900">{value.summary?.[key] ?? 0}</div></div>)}
      </div>
      <section className="rounded-lg border bg-white p-4">
        <div><h2 className="font-semibold">Expected Walmart 810s for {shown(value.shippedDate)}</h2><p className="text-sm text-slate-500">Reconciliation is read-only. Generate and Send actions are available only in Batch 810.</p></div>
        {!rows.length ? <p className="mt-4 rounded bg-emerald-50 p-3 text-sm text-emerald-900">No expected 810 records were found for this shipped date.</p> : <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead><tr className="border-b bg-slate-50"><th className="p-2">PO</th><th className="p-2">Load</th><th className="p-2">Shipped Date</th><th className="p-2">Invoice Status</th><th className="p-2">Invoice #</th><th className="p-2">Amount</th><th className="p-2">Workflow State</th><th className="p-2">Action</th></tr></thead>
            <tbody>{rows.map((row) => {
              const actionable = row.loadId && ["canGenerate", "canPrepare", "canSend"].some((key) => row.actions?.[key] === true);
              return <tr key={`${row.custCode || ""}|${row.loadId || ""}|${row.poNumber || ""}`} className="border-b">
              <td className="p-2 font-medium">{shown(row.poNumber)}</td><td className="p-2">{shown(row.loadId)}</td><td className="p-2">{shown(row.preview?.shippedDate)}</td>
              <td className="p-2">{STATUS_LABELS[row.classification] || shown(row.classification)}</td><td className="p-2">{shown(row.generation?.invoiceNumber)}</td><td className="p-2">{shown(row.preview?.totals?.invoiceTotal)}</td>
              <td className="p-2">{STATUS_LABELS[row.workflowState] || shown(row.workflowState)}</td><td className="p-2">{actionable ? <button type="button" onClick={() => onOpenBatch(row.loadId)} className="rounded border px-2 py-1 text-blue-700">Open Batch</button> : "—"}</td>
            </tr>})}</tbody>
          </table>
        </div>}
      </section>
    </>}
  </section>;
}
